import { Broker } from '@amzn/amazonmq-opsconsole-client';
import { useEffect, useState, useMemo } from 'react';
import { getRebootProgress } from '../../../../api/api';
import { RebootEvent, WorkflowExecutionInfo } from '../../../../types';

export const LOG_GROUP_PREFIX = '/aws/vendedlogs/states/';

const sortEvents = (events: RebootEvent[]): RebootEvent[] => {
    return [...events].sort((a, b) => {
        const aTime = new Date(a.timestamp).getTime();
        const bTime = new Date(b.timestamp).getTime();
        return bTime - aTime;
    });
};

const extractUniqueArnLogGroupPairs = (
    events: RebootEvent[],
): WorkflowExecutionInfo[] => {
    const executionMap = new Map<string, WorkflowExecutionInfo>();

    events.forEach(event => {
        if (event.execution_arn && !executionMap.has(event.execution_arn)) {
            executionMap.set(event.execution_arn, {
                arn: event.execution_arn,
                logGroup: event.logGroup
            });
        }
    });

    return Array.from(executionMap.values());
};

interface Props {
    broker: Broker,
    timestampFrom: number;
    timestampTo: number;
    refreshCnt: number;
}

export const useWorkflow = ({
    broker,
    timestampFrom,
    timestampTo,
    refreshCnt,
}: Props) => {
    const [events, setEvents] = useState<RebootEvent[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        try {
            setLoading(true);
            setError(null);
            getRebootProgress(broker.id, timestampFrom, timestampTo)
                .then(response => {
                    const events = response.events as RebootEvent[] || [];
                    const sortedEvents = sortEvents(events);
                    setEvents(sortedEvents);
                })
                .catch(error => {
                    console.error(error);
                    setError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            console.error(error);
            setError(error instanceof Error ? error : new Error('Unknown error occurred'));
        } finally {
            setLoading(false);
        }
    }, [broker, timestampFrom, timestampTo, refreshCnt]);

    const executions = useMemo(() => {
        return events ? extractUniqueArnLogGroupPairs(events) : [];
    }, [events, timestampFrom, timestampTo]);

    return { events, loading, error, executions};
};
